/**
 * PRpillar fork: native rendering for the "Code" chart type.
 *
 * The component's code still runs in the sandboxed iframe, but instead of
 * drawing it can call `lightdash.render(spec)`: a JSON tree of Lightdash UI
 * nodes. The host draws that tree with its own React components (Mantine
 * with the Lightdash theme, the ECharts engine of `@lightdash/visualization`),
 * outside the iframe. No code and no markup cross the boundary: every node
 * type, prop and enum value is checked here, strings render as text, and
 * event handlers stay in the iframe behind numeric action ids.
 */
export const CUSTOM_CODE_NATIVE_SPEC_VERSION = 1;
/** A tree larger than this is dropped: the host renders every node. */
export const CUSTOM_CODE_NATIVE_MAX_NODES = 2000;
export const CUSTOM_CODE_NATIVE_MAX_DEPTH = 24;
const MAX_TEXT_LENGTH = 2000;
const MAX_LIST_LENGTH = 500;
const SIZES = ['xs', 'sm', 'md', 'lg', 'xl'];
const SPACINGS = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
/** Mantine colors of the Lightdash theme, plus the field-type colors. */
const COLORS = [
    'gray',
    'red',
    'pink',
    'grape',
    'violet',
    'indigo',
    'blue',
    'cyan',
    'teal',
    'green',
    'lime',
    'yellow',
    'orange',
    'dimension',
    'metric',
    'calculation',
];
const BADGE_VARIANTS = [
    'light',
    'filled',
    'outline',
    'dot',
    'default',
];
const BUTTON_VARIANTS = [
    'default',
    'filled',
    'light',
    'subtle',
    'outline',
];
const CHART_TYPES = ['bar', 'line', 'area', 'scatter', 'pie'];
const JUSTIFY = ['flex-start', 'center', 'flex-end', 'space-between'];
const ALIGN = ['flex-start', 'center', 'flex-end', 'stretch'];
const MAX_OPTIONS = 1000;
const MAX_MARKS = 50;
const MAX_AFFIX_LENGTH = 16;
/** Default wait before a dragged slider or a typed number reaches the component */
export const CUSTOM_CODE_NATIVE_INPUT_DEBOUNCE_MS = 200;
const ISO_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
class InvalidSpec extends Error {
}
const fail = (path, what) => {
    throw new InvalidSpec(`${path}: ${what}`);
};
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value, path) => {
    if (typeof value === 'number' && Number.isFinite(value))
        return String(value);
    if (typeof value !== 'string')
        fail(path, 'expected a string');
    return value.slice(0, MAX_TEXT_LENGTH);
};
const optional = (raw, key, path, read) => raw[key] === undefined || raw[key] === null
    ? undefined
    : read(raw[key], `${path}.${key}`);
const oneOf = (allowed) => (value, path) => {
    if (!allowed.includes(value))
        fail(path, `expected one of ${allowed.join(', ')}`);
    return value;
};
const bool = (value, path) => {
    if (typeof value !== 'boolean')
        fail(path, 'expected a boolean');
    return value;
};
const int = (min, max) => (value, path) => {
    if (!Number.isInteger(value) ||
        value < min ||
        value > max)
        fail(path, `expected an integer ${min}..${max}`);
    return value;
};
const finite = (value, path) => {
    if (typeof value !== 'number' || !Number.isFinite(value))
        fail(path, 'expected a number');
    return value;
};
const list = (value, path, read) => {
    if (!Array.isArray(value))
        fail(path, 'expected an array');
    const items = value;
    if (items.length > MAX_LIST_LENGTH * 20)
        fail(path, 'too many items');
    return items.map((item, i) => read(item, `${path}[${i}]`));
};
const fieldId = (value, path) => {
    if (typeof value !== 'string' || value.length === 0 || value.length > 255)
        fail(path, 'expected a field id');
    return value;
};
const actionId = int(1, Number.MAX_SAFE_INTEGER);
const scalar = (value, path) => {
    if (value === null ||
        typeof value === 'string' ||
        typeof value === 'boolean' ||
        (typeof value === 'number' && Number.isFinite(value)))
        return typeof value === 'string'
            ? value.slice(0, MAX_TEXT_LENGTH)
            : value;
    return fail(path, 'expected a string, number, boolean or null');
};
const action = (value, path) => {
    if (typeof value === 'number')
        return actionId(value, path);
    if (!isRecord(value))
        return fail(path, 'expected an action');
    if (value.type === 'drillDown')
        return {
            type: 'drillDown',
            row: int(0, Number.MAX_SAFE_INTEGER)(value.row, `${path}.row`),
            field: optional(value, 'field', path, fieldId),
        };
    if (value.type === 'setFilter')
        return {
            type: 'setFilter',
            field: fieldId(value.field, `${path}.field`),
            value: scalar(value.value, `${path}.value`),
        };
    return fail(path, 'unknown action');
};
const isoDate = (value, path) => {
    if (typeof value !== 'string' || !ISO_DATE.test(value))
        fail(path, 'expected a date as YYYY-MM-DD');
    return value;
};
const affix = (value, path) => {
    const result = text(value, path);
    if (result.length > MAX_AFFIX_LENGTH)
        fail(path, `longer than ${MAX_AFFIX_LENGTH} characters`);
    return result;
};
const option = (item, path) => isRecord(item)
    ? {
        value: text(item.value, `${path}.value`),
        label: text(item.label ?? item.value, `${path}.label`),
    }
    : { value: text(item, path), label: text(item, path) };
const inputBase = (value, path) => ({
    label: optional(value, 'label', path, text),
    description: optional(value, 'description', path, text),
    placeholder: optional(value, 'placeholder', path, text),
    size: optional(value, 'size', path, oneOf(SIZES)),
    disabled: optional(value, 'disabled', path, bool),
    width: optional(value, 'width', path, int(40, 2000)),
    onChange: optional(value, 'onChange', path, actionId),
});
const debounce = (value, path) => optional(value, 'debounceMs', path, int(0, 5000));
const rowIndices = (value, path) => list(value, path, int(0, Number.MAX_SAFE_INTEGER));
const parseNode = (value, path, budget, depth) => {
    // The budget is shared by the whole walk on purpose
    // eslint-disable-next-line no-param-reassign
    budget.nodes += 1;
    if (budget.nodes > CUSTOM_CODE_NATIVE_MAX_NODES)
        fail(path, 'too many nodes');
    if (depth > CUSTOM_CODE_NATIVE_MAX_DEPTH)
        fail(path, 'nested too deep');
    if (typeof value === 'string' || typeof value === 'number')
        return text(value, path);
    if (!isRecord(value))
        return fail(path, 'expected a node');
    const children = () => list(value.children ?? [], `${path}.children`, (child, p) => parseNode(child, p, budget, depth + 1));
    const spacing = (key) => optional(value, key, path, oneOf(SPACINGS));
    const color = () => optional(value, 'color', path, oneOf(COLORS));
    const size = () => optional(value, 'size', path, oneOf(SIZES));
    switch (value.type) {
        case 'Stack':
            return {
                type: 'Stack',
                gap: spacing('gap'),
                align: optional(value, 'align', path, oneOf(ALIGN)),
                children: children(),
            };
        case 'Group':
            return {
                type: 'Group',
                gap: spacing('gap'),
                justify: optional(value, 'justify', path, oneOf(JUSTIFY)),
                align: optional(value, 'align', path, oneOf(ALIGN)),
                wrap: optional(value, 'wrap', path, bool),
                grow: optional(value, 'grow', path, bool),
                children: children(),
            };
        case 'Grid':
            return {
                type: 'Grid',
                cols: int(1, 12)(value.cols ?? 1, `${path}.cols`),
                gap: spacing('gap'),
                children: children(),
            };
        case 'Card':
            return {
                type: 'Card',
                padding: spacing('padding'),
                withBorder: optional(value, 'withBorder', path, bool),
                onClick: optional(value, 'onClick', path, action),
                children: children(),
            };
        case 'Divider':
            return {
                type: 'Divider',
                label: optional(value, 'label', path, text),
            };
        case 'Title':
            return {
                type: 'Title',
                order: optional(value, 'order', path, oneOf([1, 2, 3, 4, 5, 6])),
                text: text(value.text, `${path}.text`),
            };
        case 'Text':
            return {
                type: 'Text',
                text: text(value.text, `${path}.text`),
                size: size(),
                dimmed: optional(value, 'dimmed', path, bool),
                weight: optional(value, 'weight', path, oneOf([400, 500, 600, 700])),
                color: color(),
            };
        case 'Badge':
            return {
                type: 'Badge',
                text: text(value.text, `${path}.text`),
                color: color(),
                variant: optional(value, 'variant', path, oneOf(BADGE_VARIANTS)),
                size: size(),
            };
        case 'Button':
            return {
                type: 'Button',
                label: text(value.label, `${path}.label`),
                variant: optional(value, 'variant', path, oneOf(BUTTON_VARIANTS)),
                color: color(),
                size: size(),
                disabled: optional(value, 'disabled', path, bool),
                onClick: optional(value, 'onClick', path, action),
            };
        case 'SegmentedControl':
            return {
                type: 'SegmentedControl',
                data: list(value.data, `${path}.data`, (item, p) => isRecord(item)
                    ? {
                        value: text(item.value, `${p}.value`),
                        label: text(item.label ?? item.value, `${p}.label`),
                    }
                    : { value: text(item, p), label: text(item, p) }).slice(0, 20),
                value: text(value.value, `${path}.value`),
                size: size(),
                onChange: optional(value, 'onChange', path, actionId),
            };
        case 'Select': {
            const multiple = optional(value, 'multiple', path, bool);
            const data = optional(value, 'data', path, (v, p) => {
                const items = list(v, p, option);
                if (items.length > MAX_OPTIONS)
                    fail(p, `more than ${MAX_OPTIONS} options`);
                return items;
            });
            const field = optional(value, 'field', path, fieldId);
            if (!data && !field)
                fail(path, 'Select needs data or a field');
            const single = multiple || value.value === null || value.value === undefined
                ? null
                : text(value.value, `${path}.value`);
            const selected = multiple
                ? list(value.value ?? [], `${path}.value`, text)
                : single;
            return {
                type: 'Select',
                ...inputBase(value, path),
                data,
                field,
                multiple,
                value: selected,
                searchable: optional(value, 'searchable', path, bool),
                clearable: optional(value, 'clearable', path, bool),
                maxValues: optional(value, 'maxValues', path, int(1, MAX_OPTIONS)),
            };
        }
        case 'Slider': {
            const min = finite(value.min, `${path}.min`);
            const max = finite(value.max, `${path}.max`);
            if (!(max > min))
                fail(`${path}.max`, 'must be greater than min');
            const step = optional(value, 'step', path, finite);
            if (step !== undefined && !(step > 0 && step <= max - min))
                fail(`${path}.step`, 'must be positive and fit in min..max');
            return {
                type: 'Slider',
                ...inputBase(value, path),
                min,
                max,
                step,
                value: Math.min(Math.max(finite(value.value, `${path}.value`), min), max),
                marks: optional(value, 'marks', path, (v, p) => {
                    const items = list(v, p, (m, mp) => isRecord(m)
                        ? {
                            value: finite(m.value, `${mp}.value`),
                            label: optional(m, 'label', mp, text),
                        }
                        : { value: finite(m, mp) });
                    if (items.length > MAX_MARKS)
                        fail(p, `more than ${MAX_MARKS} marks`);
                    return items;
                }),
                prefix: optional(value, 'prefix', path, affix),
                suffix: optional(value, 'suffix', path, affix),
                decimals: optional(value, 'decimals', path, int(0, 10)),
                debounceMs: debounce(value, path),
            };
        }
        case 'NumberInput': {
            const min = optional(value, 'min', path, finite);
            const max = optional(value, 'max', path, finite);
            if (min !== undefined && max !== undefined && !(max >= min))
                fail(`${path}.max`, 'must not be less than min');
            return {
                type: 'NumberInput',
                ...inputBase(value, path),
                value: value.value === null || value.value === undefined
                    ? null
                    : finite(value.value, `${path}.value`),
                min,
                max,
                step: optional(value, 'step', path, (v, p) => {
                    const n = finite(v, p);
                    if (!(n > 0))
                        fail(p, 'must be positive');
                    return n;
                }),
                prefix: optional(value, 'prefix', path, affix),
                suffix: optional(value, 'suffix', path, affix),
                decimalScale: optional(value, 'decimalScale', path, int(0, 10)),
                thousandSeparator: optional(value, 'thousandSeparator', path, bool),
                debounceMs: debounce(value, path),
            };
        }
        case 'DateInput': {
            const range = optional(value, 'range', path, bool);
            const maybeDate = (v, p) => v === null || v === undefined ? null : isoDate(v, p);
            let selected;
            if (range) {
                const pair = value.value ?? [null, null];
                if (!Array.isArray(pair) || pair.length !== 2)
                    fail(`${path}.value`, 'expected [start, end]');
                const [start, end] = pair;
                selected = [
                    maybeDate(start, `${path}.value[0]`),
                    maybeDate(end, `${path}.value[1]`),
                ];
            }
            else {
                selected = maybeDate(value.value, `${path}.value`);
            }
            return {
                type: 'DateInput',
                ...inputBase(value, path),
                range,
                value: selected,
                minDate: optional(value, 'minDate', path, isoDate),
                maxDate: optional(value, 'maxDate', path, isoDate),
                firstDayOfWeek: optional(value, 'firstDayOfWeek', path, oneOf([0, 1, 2, 3, 4, 5, 6])),
            };
        }
        case 'Tooltip':
            return {
                type: 'Tooltip',
                label: text(value.label, `${path}.label`),
                child: parseNode(value.child, `${path}.child`, budget, depth + 1),
            };
        case 'BigNumber': {
            const node = {
                type: 'BigNumber',
                field: optional(value, 'field', path, fieldId),
                value: optional(value, 'value', path, (v, p) => typeof v === 'number' ? finite(v, p) : text(v, p)),
                label: optional(value, 'label', path, text),
                delta: optional(value, 'delta', path, finite),
                deltaGood: optional(value, 'deltaGood', path, oneOf(['up', 'down'])),
                onClick: optional(value, 'onClick', path, action),
            };
            if (node.field === undefined && node.value === undefined)
                fail(path, 'BigNumber needs a field or a value');
            return node;
        }
        case 'Table':
            return {
                type: 'Table',
                columns: optional(value, 'columns', path, (v, p) => list(v, p, fieldId)),
                rows: optional(value, 'rows', path, rowIndices),
                data: optional(value, 'data', path, (v, p) => {
                    if (!isRecord(v))
                        return fail(p, 'expected table data');
                    return {
                        columns: list(v.columns, `${p}.columns`, (c, cp) => isRecord(c)
                            ? {
                                id: text(c.id, `${cp}.id`),
                                label: text(c.label ?? c.id, `${cp}.label`),
                                align: optional(c, 'align', cp, oneOf(['left', 'right'])),
                            }
                            : fail(cp, 'expected a column')),
                        rows: list(v.rows, `${p}.rows`, (r, rp) => list(r, rp, scalar)),
                    };
                }),
                maxHeight: optional(value, 'maxHeight', path, int(40, 10000)),
                striped: optional(value, 'striped', path, bool),
                onRowClick: optional(value, 'onRowClick', path, (v, p) => v === 'drillDown' ? 'drillDown' : action(v, p)),
            };
        case 'Chart':
            return {
                type: 'Chart',
                chart: oneOf(CHART_TYPES)(value.chart, `${path}.chart`),
                x: fieldId(value.x, `${path}.x`),
                y: list(value.y, `${path}.y`, fieldId).slice(0, 20),
                stack: optional(value, 'stack', path, bool),
                horizontal: optional(value, 'horizontal', path, bool),
                rows: optional(value, 'rows', path, rowIndices),
                height: optional(value, 'height', path, int(80, 2000)),
            };
        default:
            return fail(path, `unknown node type ${String(value.type)}`);
    }
};
/**
 * Checks a spec from the component. Returns the spec rebuilt from the
 * allowed keys only, or the reason it was refused.
 */
export const parseCustomCodeNativeSpec = (value) => {
    try {
        if (!isRecord(value))
            return { error: 'spec: expected an object' };
        if (value.version !== CUSTOM_CODE_NATIVE_SPEC_VERSION)
            return {
                error: `spec: version ${CUSTOM_CODE_NATIVE_SPEC_VERSION} expected`,
            };
        return {
            spec: {
                version: CUSTOM_CODE_NATIVE_SPEC_VERSION,
                root: parseNode(value.root, 'root', { nodes: 0 }, 0),
            },
        };
    }
    catch (e) {
        if (e instanceof InvalidSpec)
            return { error: e.message };
        throw e;
    }
};
//# sourceMappingURL=customCodeNative.js.map